import {
  InternalUsersService,
  RemoveUserRoleRequest,
  RemoveUserRoleResponse,
  UpdateUserRoleRequest,
  UpdateUserRoleResponse,
  type AcceptInternalInvitationRequest,
  type AcceptInternalInvitationResponse,
  type CompletePasswordResetRequest,
  type InternalUser,
  type InternalUsersFilters,
  type InviteUserRequest,
  type InviteUserResponse,
  type PaginatedInternalUsersResponse,
  type RequestPasswordResetRequest,
  type UpdateInternalProfileRequest,
  type UpdateInternalProfileResponse,
  type VerifyInviteResponse,
  type VerifyResetTokenResponse,
} from "@/lib/api/internalUsers.service";
import { queryClient } from "@/lib/queryClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "./use-toast";

export type {
  CompletePasswordResetRequest,
  InternalUser,
  InternalUsersFilters,
  PaginatedInternalUsersResponse,
  RequestPasswordResetRequest,
  UpdateInternalProfileRequest,
  UpdateInternalProfileResponse,
  VerifyInviteResponse,
  VerifyResetTokenResponse,
};

export const useInternalUsers = (filters?: InternalUsersFilters) => {
  return useQuery<PaginatedInternalUsersResponse>({
    queryKey: ["/api/admin/internal-users", filters],
    queryFn: () => InternalUsersService.getInternalUsers(filters),
  });
};

export const useVerifyInvite = (token: string | null) => {
  return useQuery<VerifyInviteResponse>({
    queryKey: ["/api/admin/invite", token],
    queryFn: () => InternalUsersService.verifyInvite(token!),
    enabled: !!token,
    retry: false,
  });
};

export const useInviteInternalUser = () => {
  const queryClient = useQueryClient();

  return useMutation<InviteUserResponse, Error, InviteUserRequest>({
    mutationFn: InternalUsersService.inviteInternalUser,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/internal-users"],
      });
    },
  });
};

export const useAcceptInternalInvitation = () => {
  return useMutation<
    AcceptInternalInvitationResponse,
    Error,
    AcceptInternalInvitationRequest
  >({
    mutationFn: InternalUsersService.acceptInternalInvitation,
  });
};

export const useUpdateInternalProfile = () => {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateInternalProfileResponse,
    Error,
    UpdateInternalProfileRequest
  >({
    mutationFn: InternalUsersService.updateInternalProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/internal-users"],
      });
    },
  });
};

export const useChangeOwnPassword = () =>
  useMutation({ mutationFn: InternalUsersService.changeOwnPassword });

export const useRequestPasswordReset = (onSuccess?: () => void) => {
  return useMutation<{ message: string }, Error, RequestPasswordResetRequest>({
    mutationFn: InternalUsersService.requestPasswordReset,
    onSuccess: () => {
      toast({
        title: "Password reset email sent",
        description: "A password reset link has been sent to the user",
      });
      onSuccess?.();
    },
    onError: (error: Error) => {
      const errorMessage =
        error?.message || "Failed to send password reset email";
      toast({
        title: "Error sending password reset",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });
};

export const useForgotPassword = () => {
  return useMutation<{ message: string }, Error, RequestPasswordResetRequest>({
    mutationFn: InternalUsersService.forgotPassword,
  });
};

export const useVerifyResetToken = (token: string | null) => {
  return useQuery<VerifyResetTokenResponse>({
    queryKey: ["/api/admin/reset-password", token],
    queryFn: () => InternalUsersService.verifyResetToken(token!),
    enabled: !!token,
    retry: false,
  });
};

export const useCompletePasswordReset = () => {
  return useMutation<{ message: string }, Error, CompletePasswordResetRequest>({
    mutationFn: InternalUsersService.completePasswordReset,
    onSuccess: () => {
      toast({
        title: "Password reset successful",
        description: "Your password has been reset successfully",
      });
    },
    onError: (error: Error) => {
      const errorMessage = error?.message || "Failed to reset password";
      toast({
        title: "Error resetting password",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });
};

const invalidateUsers = () => {
  queryClient.invalidateQueries({
    predicate: (query) => query.queryKey[0] === "/api/admin/internal-users",
  });
  queryClient.invalidateQueries({
    predicate: (query) => query.queryKey[0] === "/admin/users",
  });
};

export const useSuspendUser = (onSuccess?: () => void) => {
  return useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason?: string }) =>
      InternalUsersService.suspendUser(userId, reason),
    onSuccess: () => {
      invalidateUsers();
      toast({
        title: "User suspended",
        description: "The user has been suspended successfully",
      });
      onSuccess?.();
    },
    onError: (error: Error) => {
      const errorMessage = error?.message || "Failed to suspend user";
      toast({
        title: "Error suspending user",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });
};

export const useRestoreUser = (onSuccess?: () => void) => {
  return useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason?: string }) =>
      InternalUsersService.restoreUser(userId, reason),
    onSuccess: () => {
      invalidateUsers();
      toast({
        title: "User restored",
        description: "The user has been restored to active status",
      });
      onSuccess?.();
    },
    onError: (error: Error) => {
      const errorMessage = error?.message || "Failed to restore user";
      toast({
        title: "Error restoring user",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });
};

export const useDeleteUser = (onSuccess?: () => void) => {
  return useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason?: string }) =>
      InternalUsersService.deleteUser(userId, reason),
    onSuccess: () => {
      invalidateUsers();
      toast({
        title: "User deleted",
        description: "The user has been deleted successfully",
        variant: "destructive",
      });
      onSuccess?.();
    },
    onError: (error: Error) => {
      const errorMessage = error?.message || "Failed to delete user";
      toast({
        title: "Error deleting user",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });
};

export const useResendInvitation = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ invitationId }: { invitationId: string }) =>
      InternalUsersService.resendInvitation(invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/internal-users"],
      });
      toast({
        title: "Invitation resent",
        description: "A new invitation email has been sent successfully",
      });
      onSuccess?.();
    },
    onError: (error: Error) => {
      const errorMessage = error?.message || "Failed to resend invitation";
      toast({
        title: "Error resending invitation",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });
};

export const useCancelInvitation = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ invitationId }: { invitationId: string }) =>
      InternalUsersService.cancelInvitation(invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/internal-users"],
      });
      toast({
        title: "Invitation cancelled",
        description:
          "The invitation has been cancelled and the user has been notified",
        variant: "destructive",
      });
      onSuccess?.();
    },
    onError: (error: Error) => {
      const errorMessage = error?.message || "Failed to cancel invitation";
      toast({
        title: "Error cancelling invitation",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });
};

export const useUpdateUserRole = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateUserRoleResponse,
    Error,
    { userId: string; data: UpdateUserRoleRequest }
  >({
    mutationFn: ({ userId, data }) =>
      InternalUsersService.updateUserRole(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/internal-users"],
      });
      toast({
        title: "User role updated",
        description: "The user's role has been updated successfully",
      });
      onSuccess?.();
    },
    onError: (error: Error) => {
      const errorMessage = error?.message || "Failed to update user role";
      toast({
        title: "Error updating user role",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });
};

export const useRemoveUserRole = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation<
    RemoveUserRoleResponse,
    Error,
    { userId: string; data: RemoveUserRoleRequest }
  >({
    mutationFn: ({ userId, data }) =>
      InternalUsersService.removeUserRole(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/internal-users"],
      });
      toast({
        title: "Role removed",
        description: "The role has been removed successfully",
      });
      onSuccess?.();
    },
    onError: (error: Error) => {
      const errorMessage = error?.message || "Failed to remove user role";
      toast({
        title: "Error removing role",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });
};
